/**
 * Converter: persisted structural audit rows → @agion/ui AuditEntry[].
 *
 * The durable trail (see services/audit-api.ts) is content-free: it has no
 * prompts, responses or tool payloads. Entries are built with whatever
 * structural facts exist (tool/model names, tokens, durations, outcomes)
 * and localized placeholders where the schema requires content fields.
 *
 * Event types follow admin-ui's EVENT_TYPE_CONFIG (MissionAuditTab.tsx).
 * Unknown event types are skipped rather than guessed.
 */

import { isStoppedRunEnd, mergeToolResults } from '@agion/ui'
import type {
  AuditEntry,
  HITLModality,
  ToolCallAuditEntry,
} from '@agion/ui'
import type { MissionAuditEventRow } from '../services/audit-api'
import { neutralEnding, type NeutralEnding } from './neutral-endings'

export interface PersistedAuditLabels {
  /** Placeholder for HITL prompt/response content stored on the Edge host */
  contentOnHost: string
  /** Label for mission start/end pseudo-nodes */
  mission: string
  /** Sentence for each neutral ending (utils/neutral-endings.ts) */
  neutralEndings: Record<NeutralEnding, string>
  /**
   * The plain sentence of a failed ending, given the row's stored code: the
   * live view's words (utils/run-end.ts failedRunText), never the raw
   * category or error text (agion/Agion#2946).
   */
  runFailed: (code: string | undefined) => string
}

/** The stored code a failed ending row carries: its outcome, else its category. */
function endingCode(row: MissionAuditEventRow): string | undefined {
  return row.outcome ?? row.error_category ?? undefined
}

/**
 * Whether the trail ends in a failure whose sentence the run's end record can
 * word: a failed mission_end or an error row. A run a person stopped did not
 * fail (agion/Agion#2947).
 */
export function trailHasFailedEnding(rows: MissionAuditEventRow[]): boolean {
  return rows.some(
    (row) =>
      (row.event_type === 'mission_end' && row.success === false && !isStoppedRunEnd(row.event_type, row.decision)) ||
      row.event_type === 'error',
  )
}

export function convertPersistedAuditRows(
  rows: MissionAuditEventRow[],
  labels: PersistedAuditLabels,
): AuditEntry[] {
  const entries: AuditEntry[] = []
  const turnCounters = new Map<string, number>()
  // Every row has its own id and nothing joins a result to its call, so a
  // result answers the earliest still-open call of the same tool on the same
  // node (agion/Agion#2481).
  const openCalls = new Map<string, string[]>()
  const callKey = (row: MissionAuditEventRow) => `${row.node_id ?? ''}|${row.tool_name ?? 'tool'}`

  for (const row of rows) {
    const base = {
      id: row.id,
      timestamp: Date.parse(row.created_at) || 0,
      nodeId: row.node_id ?? '',
      // governance-service emits empty strings, not nulls (verified live) —
      // coerce so the UI falls back to its own label resolution
      agentId: row.agent_id || undefined,
      agentLabel: row.agent_name || undefined,
      userName: row.user_display_name || undefined,
    }

    switch (row.event_type) {
      case 'agent_turn': {
        const nodeKey = base.nodeId || 'mission'
        const turnNumber = (turnCounters.get(nodeKey) ?? 0) + 1
        turnCounters.set(nodeKey, turnNumber)
        entries.push({
          ...base,
          type: 'turn',
          turnNumber,
          inputTokens: row.tokens_input ?? 0,
          outputTokens: row.tokens_output ?? 0,
          durationMs: row.duration_ms ?? 0,
          model: row.model_name ?? undefined,
        })
        break
      }

      case 'tool_call': {
        const key = callKey(row)
        openCalls.set(key, [...(openCalls.get(key) ?? []), row.id])
        entries.push({
          ...base,
          type: 'tool_call',
          toolCallId: row.id,
          toolName: row.tool_name ?? 'tool',
          // Content-free trail — tool input stays on the Edge host
          toolInput: {},
        })
        break
      }

      case 'tool_result': {
        const open = openCalls.get(callKey(row)) ?? []
        const callId = open.shift()
        entries.push({
          ...base,
          type: 'tool_result',
          toolCallId: callId ?? row.id,
          toolName: row.tool_name ?? 'tool',
          success: row.success ?? true,
          error: row.success === false ? (row.error_category ?? undefined) : undefined,
          durationMs: row.duration_ms ?? 0,
        })
        break
      }

      case 'hitl_request':
        entries.push({
          ...base,
          type: 'hitl_request',
          requestId: row.hitl_request_id ?? row.id,
          modality: 'text' as HITLModality,
          prompt: labels.contentOnHost,
          pending: false,
        })
        break

      case 'hitl_response':
        entries.push({
          ...base,
          type: 'hitl_response',
          requestId: row.hitl_request_id ?? row.id,
          modality: 'text' as HITLModality,
          response: labels.contentOnHost,
          responseTimeMs: row.duration_ms ?? 0,
        })
        break

      case 'mission_start':
        entries.push({
          ...base,
          type: 'node_start',
          nodeLabel: labels.mission,
        })
        break

      case 'mission_end': {
        // A run a person stopped, as the server recorded it on the end row's
        // decision, reads Stopped, never Failed (agion/Agion#2947).
        if (isStoppedRunEnd(row.event_type, row.decision)) {
          entries.push({
            ...base,
            type: 'node_complete',
            nodeLabel: labels.mission,
            success: false,
            stopped: true,
            durationMs: row.duration_ms ?? undefined,
          })
          break
        }
        // A neutral ending (finished with an answer to check) is a warning,
        // matching the chat's notice, not a failed node.
        const neutral = row.success === false ? neutralEnding(row.error_category) : undefined
        if (neutral) {
          entries.push({
            ...base,
            // The run's own ending: named as the mission, never an unnamed step.
            agentLabel: base.agentLabel ?? labels.mission,
            type: 'error',
            severity: 'warning',
            message: labels.neutralEndings[neutral],
          })
          break
        }
        entries.push({
          ...base,
          type: 'node_complete',
          nodeLabel: labels.mission,
          success: row.success ?? true,
          error: row.success === false ? labels.runFailed(endingCode(row)) : undefined,
          durationMs: row.duration_ms ?? undefined,
        })
        break
      }

      case 'error':
        entries.push({
          ...base,
          type: 'error',
          message: labels.runFailed(endingCode(row)),
        })
        break

      default:
        // Unknown / control-plane event types (e.g. context.learning.injected)
        // have no chat-facing rendering — skip rather than guess.
        break
    }
  }

  return entries
}

/**
 * A persisted trail describes a finished run, so no call in it is in flight:
 * each result folds into its call, and a call whose result was not recorded
 * is still finished, with no duration (agion/Agion#2481).
 */
export function finishPersistedTrail(entries: AuditEntry[]): AuditEntry[] {
  const merged = mergeToolResults(entries)
  for (const e of merged) {
    if (e.type === 'tool_call') {
      const call = e as ToolCallAuditEntry
      if (!call.resolved) call.resolved = true
    }
  }
  return merged
}

const TOOL_TYPES = new Set<AuditEntry['type']>(['tool_call', 'tool_result'])
const QUESTION_TYPES = new Set<AuditEntry['type']>(['hitl_request', 'hitl_response'])

/**
 * A reopened run from both sources (agion/Agion#2481): the Edge history holds
 * the content (tool arguments, outputs, question text) and the persisted trail
 * holds the structure (turns with model and tokens, the run's start and end).
 * Tool and question entries come from the history when it has them; every
 * other entry comes from the trail. A history call without a recorded
 * duration takes the trail's for the same tool, in order, and is finished.
 * Without history tool entries (none recorded, or the history failed) the
 * finished trail stands alone.
 */
export function combineReopenedRun(history: AuditEntry[], trail: AuditEntry[]): AuditEntry[] {
  const historyTools = history.filter((e) => TOOL_TYPES.has(e.type))
  if (historyTools.length === 0) return trail
  const historyHasQuestions = history.some((e) => QUESTION_TYPES.has(e.type))

  const trailDurations = new Map<string, number[]>()
  for (const e of trail) {
    if (e.type !== 'tool_call') continue
    const call = e as ToolCallAuditEntry
    const queue = trailDurations.get(call.toolName) ?? []
    queue.push(call.resolvedDurationMs ?? 0)
    trailDurations.set(call.toolName, queue)
  }

  const fromHistory = history
    .filter((e) => TOOL_TYPES.has(e.type) || QUESTION_TYPES.has(e.type))
    .map((e) => {
      if (e.type !== 'tool_call') return e
      const call = { ...(e as ToolCallAuditEntry) }
      const recorded = trailDurations.get(call.toolName)?.shift()
      if (!call.resolvedDurationMs && recorded) call.resolvedDurationMs = recorded
      call.resolved = true
      return call
    })
  const fromTrail = trail.filter(
    (e) => !TOOL_TYPES.has(e.type) && !(historyHasQuestions && QUESTION_TYPES.has(e.type)),
  )
  return [...fromTrail, ...fromHistory].sort((a, b) => a.timestamp - b.timestamp)
}
